import React, { useState, useCallback } from "react";
import CloudButtonHandler from "./CloudButtonHandler";
import "../styles/yolo.css";
import { v4 as uuidv4 } from "uuid";
import { Box, Button, Modal, Slider, Typography, Alert, ButtonGroup, Chip, CircularProgress } from "@mui/material";
import { BASE_URL } from "../constants/global";
import { getImageAnalyzeUrl, getImageUploadUrl } from "../utils/imageUrls";
import axios from "axios";
import { CircularProgressWithLabel } from "./CircularProgressWithLabel";
import { LinearProgress } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import "react-image-crop/dist/ReactCrop.css";
import ReactCrop, { Crop } from "react-image-crop";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ZoomModal from "./ZoomModal";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import EggCountDisplay from "./EggCountDisplay";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
type fileNameProps = {
  setFileName: (arg: string) => void;
  setComputedEggCount: (arg: number) => void;
  setImageSubmitted: (arg: boolean) => void;
  setAnalyzedResult: (arg: any) => void;
};

export const CloudYolo: React.FC<fileNameProps> = (props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [imagesSubmitted, setImagesSubmitted] = useState<boolean>(false);
  const [imageAnalyzed, setImageAnalyzed] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);
  const [computedEggCount, setComputedEggCount] = useState<number>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [jsonModalOpen, setJsonModalOpen] = useState<boolean>(false);
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(0.25);
  const [crop, setCrop] = useState<Crop>();
  const [aspectRatio, setAspectRatioState] = useState<number | undefined>(undefined);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [activeImageLabel, setActiveImageLabel] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<any>(null);
  const [isReanalyzing, setIsReanalyzing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cropDimensions, setCropDimensions] = useState<{ width: number; height: number } | null>(null);

  const handleModalOpen = (image: string, label?: string) => {
    setActiveImage(image);
    if (label) {
      setActiveImageLabel(label);
    }
    setModalOpen(true);
  };

  const handleModalClose = () => setModalOpen(false);

  const handleJsonModalOpen = () => setJsonModalOpen(true);

  const onImageLoad = useCallback((img: HTMLImageElement) => {
    const loadImage = () => {
      const aspect = 16 / 9;
      const width = img.width;
      const height = width / aspect;
      setCrop({
        unit: "%",
        width: 100,
        height: (100 * height) / width,
        x: 0,
        y: 0,
      });
    };

    if (img.complete) {
      loadImage();
    } else {
      img.onload = loadImage;
    }
  }, []);
  const getCroppedImg = useCallback(
    (
      image: HTMLImageElement | null,
      crop: Crop
    ): Promise<string | undefined> => {
      return new Promise((resolve) => {
        if (!image) {
          console.error("Image not found");
          resolve(undefined);
          return;
        }

        if (!image.complete) {
          image.onload = () => {
            resolve(cropImage(image, crop));
          };
          image.onerror = () => {
            console.error("Error loading image");
            resolve(undefined);
          };
        } else {
          resolve(cropImage(image, crop));
        }
      });
    },
    []
  );

  const cropImage = (
    image: HTMLImageElement,
    crop: Crop
  ): Promise<string | undefined> => {
    return new Promise<string | undefined>((resolve) => {
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Use the original image dimensions for the canvas
      canvas.width = crop.width! * scaleX;
      canvas.height = crop.height! * scaleY;

      const ctx = canvas.getContext("2d");

      ctx!.drawImage(
        image,
        crop.x! * scaleX,
        crop.y! * scaleY,
        crop.width! * scaleX,
        crop.height! * scaleY,
        0,
        0,
        crop.width! * scaleX,
        crop.height! * scaleY
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            resolve(undefined);
            return;
          }
          const croppedImageUrl = window.URL.createObjectURL(blob);
          resolve(croppedImageUrl);
        },
        "image/jpeg",
        1
      );
    });
  };

  const handleCropComplete = useCallback(
    async (crop: Crop) => {
      if (imageSrc && crop.width && crop.height) {
        const image = document.querySelector(
          ".ReactCrop__child-wrapper img"
        ) as HTMLImageElement | null;
        if (image) {
          const croppedImage = await getCroppedImg(image, crop);
          if (croppedImage) {
            setCroppedImageUrl(croppedImage);
            // Calculate actual pixel dimensions
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            setCropDimensions({
              width: Math.round(crop.width * scaleX),
              height: Math.round(crop.height * scaleY),
            });
          }
        } else {
          console.error("Image element not found");
        }
      }
    },
    [imageSrc, getCroppedImg]
  );

  const analyzeImage = async (imageUrl: string) => {
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const localUUID = uuidv4();
      const formData = new FormData();
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const fileName =
        blob.type === "image/jpeg"
          ? `${localUUID}.jpg`
          : `${localUUID}.${blob.type.split("/")[1]}`;
      formData.append("file", blob, fileName);
      formData.append("model_name", "aedes_yolov5n");
      formData.append("confidence_threshold", confidenceThreshold.toString());
      formData.append("idempotencyKey", localUUID);
      formData.append("include_image_base64", "true");

      const apiResponse = await axios.post(
        getImageAnalyzeUrl(),
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: any) => {
            let percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      const eggCount = apiResponse.data.egg_count;
      const predictions = apiResponse.data.predictions;
      setComputedEggCount(eggCount);
      props.setComputedEggCount(eggCount);
      if (predictions) {
        setPredictions(predictions);
        props.setAnalyzedResult(predictions);
      }
      setImageBase64(apiResponse.data.image_base64);
      setImageAnalyzed(true);
    } catch (error: any) {
      console.error("Error during image analysis:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to analyze image. Please try again.";
      setError(errorMessage);
      throw error;
    }
  };
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!croppedImageUrl) {
        setError("Please crop the image before submitting");
        return;
      }
      await analyzeImage(croppedImageUrl);
    } catch (error: any) {
      console.error("Error during image prediction:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to process image. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async () => {
    try {
      setIsReanalyzing(true);
      setImageAnalyzed(false);
      setLoading(true);
      setConfidenceThreshold(0.25);
    } catch (error) {
      console.error("Error during image reanalysis:", error);
    } finally {
      setLoading(false);
      setIsReanalyzing(false);
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const localUUID = uuidv4();
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      // Upload the analyzed image
      const analyzedBlob = await imageToBlobFromBase64(imageBase64!);
      const analyzedFormData = new FormData();
      analyzedFormData.append(
        "file",
        analyzedBlob,
        `${localUUID}_analyzed.jpg`
      );
      analyzedFormData.append("idempotencyKey", localUUID);
      analyzedFormData.append("include_image_base64", "true");
      await axios.post(getImageUploadUrl(), analyzedFormData, config);

      // Upload the original image
      const img = new Image();
      img.src = croppedImageUrl!;
      const originalBlob = await imageToBlob(img);
      const originalFormData = new FormData();
      originalFormData.append("file", originalBlob, `${localUUID}.jpg`);
      originalFormData.append("idempotencyKey", localUUID);
      originalFormData.append("include_image_base64", "true");
      await axios.post(getImageUploadUrl(), originalFormData, config);

      // Update the parent component with the file name
      props.setFileName(`${localUUID}`);
      setImagesSubmitted(true);
      props.setImageSubmitted(true);
    } catch (error: any) {
      console.error("Error during image upload:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to upload image. Please try again.";
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  async function imageToBlobFromBase64(base64: string): Promise<Blob> {
    const response = await fetch(`data:image/jpeg;base64,${base64}`);
    const blob = await response.blob();
    return blob;
  }

  async function imageToBlob(image: HTMLImageElement): Promise<Blob> {
    if (!image.complete) {
      await new Promise((resolve) => {
        image.onload = resolve;
      });
    }
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext("2d");
    context?.drawImage(image, 0, 0, image.width, image.height);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, "image/jpeg");
    });
  }

  const handleClear = () => {
    setImageSrc(null);
    setImageBase64(null);
    setComputedEggCount(0);
    setImagesSubmitted(false);
    setImageAnalyzed(false);
    setCroppedImageUrl(null);
    setPredictions(null);
    setError(null);
    setCropDimensions(null);
    setCrop(undefined);
  };

  const handleResetCrop = () => {
    if (imageSrc) {
      const img = document.querySelector(".ReactCrop__child-wrapper img") as HTMLImageElement | null;
      if (img) {
        onImageLoad(img);
      }
    }
  };

  const setAspectRatio = (ratio: number | undefined) => {
    setAspectRatioState(ratio);
    if (imageSrc && crop) {
      const img = document.querySelector(".ReactCrop__child-wrapper img") as HTMLImageElement | null;
      if (img) {
        if (ratio) {
          const width = crop.width || 100;
          const height = width / ratio;
          const newCrop: Crop = {
            ...crop,
            height: height,
          };
          setCrop(newCrop);
        } else {
          setCrop({ ...crop });
        }
      }
    }
  };

  return (
    <>
      <Box sx={{ textAlign: "center" }}>
        <Box className="Aedes-view">
          <Box className="centered-container">
          <div className="image-container">
            {!imageAnalyzed && imageSrc && (
              <Box className="image-wrapper">
                <Typography variant="h6" gutterBottom>Original Image - Crop Area</Typography>
                
                {/* Crop Controls */}
                <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                    <Typography variant="body2" sx={{ width: "100%", textAlign: "center", mb: 1 }}>
                      <AspectRatioIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                      Aspect Ratio:
                    </Typography>
                    <ButtonGroup size="small" variant="outlined">
                      <Button 
                        onClick={() => setAspectRatio(16 / 9)}
                        variant={aspectRatio === 16 / 9 ? "contained" : "outlined"}
                      >
                        16:9
                      </Button>
                      <Button 
                        onClick={() => setAspectRatio(4 / 3)}
                        variant={aspectRatio === 4 / 3 ? "contained" : "outlined"}
                      >
                        4:3
                      </Button>
                      <Button 
                        onClick={() => setAspectRatio(1)}
                        variant={aspectRatio === 1 ? "contained" : "outlined"}
                      >
                        1:1
                      </Button>
                      <Button 
                        onClick={() => setAspectRatio(undefined)}
                        variant={aspectRatio === undefined ? "contained" : "outlined"}
                      >
                        Free
                      </Button>
                    </ButtonGroup>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      startIcon={<RestartAltIcon />}
                      onClick={handleResetCrop}
                    >
                      Reset Crop
                    </Button>
                  </Box>
                  {cropDimensions && (
                    <Chip 
                      label={`Crop Size: ${cropDimensions.width} × ${cropDimensions.height} px`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>

                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={handleCropComplete}
                  aspect={aspectRatio}
                >
                  <img
                    src={imageSrc}
                    alt="Original Image"
                    onLoad={(e) => onImageLoad(e.currentTarget)}
                    style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
                  />
                </ReactCrop>
                {loading && (
                  <Box 
                    sx={{ 
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 3,
                      backdropFilter: 'blur(4px)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      zIndex: 9999
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress 
                        size={80} 
                        thickness={4}
                        sx={{ 
                          color: 'primary.main',
                          mb: 2
                        }} 
                      />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: 'white',
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        Analyzing Image
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          mb: 3
                        }}
                      >
                        Our AI is counting mosquito eggs...
                      </Typography>
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <Box sx={{ width: 300, mx: 'auto' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'white' }}>
                              Uploading
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'white' }}>
                              {uploadProgress}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress} 
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: 'primary.light'
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {imageBase64 && (
              <Box className="result-display-cards" sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                {croppedImageUrl && (
                  <Box className="image-wrapper">
                    <Typography variant="h6" gutterBottom>Cropped Image</Typography>
                    <img
                      className="yolo-image"
                      src={croppedImageUrl}
                      alt="Cropped Image"
                      onClick={() =>
                        handleModalOpen(croppedImageUrl, "Cropped Image")
                      }
                      style={{ maxWidth: "100%", height: "auto", borderRadius: "8px", cursor: "pointer" }}
                    />
                  </Box>
                )}
                {imageBase64 && (
                  <Box className="image-wrapper">
                    <Typography variant="h6" gutterBottom>Analyzed Image</Typography>
                    <img
                      className="yolo-image"
                      src={`data:image/jpeg;base64,${imageBase64}`}
                      alt="Analyzed Image"
                      onClick={() =>
                        handleModalOpen(
                          `data:image/jpeg;base64,${imageBase64}`,
                          "Analyzed Image"
                        )
                      }
                      style={{ maxWidth: "100%", height: "auto", borderRadius: "8px", cursor: "pointer" }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </div>
          <CloudButtonHandler
            imageSrcState={[imageSrc, setImageSrc]}
            setComputedEggCount={setComputedEggCount}
            imagesSubmitted={imagesSubmitted}
            clearImage={handleClear}
          />

          {/* Error Display */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mt: 2, mb: 2, display: "inline-flex", maxWidth: 600 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {imageAnalyzed && !imagesSubmitted && !isUploading && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
              <Button
                color="warning"
                variant="contained"
                onClick={handleReanalyze}
                disabled={isReanalyzing}
                size="large"
                startIcon={<RefreshIcon />}
              >
                {isReanalyzing ? "Reanalyzing..." : "Reanalyze"}
              </Button>
              <Button
                color="success"
                variant="contained"
                onClick={handleUpload}
                size="large"
                startIcon={<CloudUploadIcon />}
              >
                Upload Image
              </Button>
            </Box>
          )}

          {(!imageAnalyzed || isReanalyzing) && !loading && imageSrc && (
            <Box className="confidence-slider" sx={{ maxWidth: 400, mx: "auto", mt: 3 }}>
              <Typography id="confidence-slider" variant="body1" gutterBottom>
                Confidence Threshold: {confidenceThreshold.toFixed(2)}
              </Typography>
              <Slider
                value={confidenceThreshold}
                onChange={(_, newValue) =>
                  setConfidenceThreshold(newValue as number)
                }
                aria-labelledby="confidence-slider"
                step={0.05}
                marks
                min={0}
                max={1}
                valueLabelDisplay="auto"
              />
            </Box>
          )}

          {!imageAnalyzed && !loading && croppedImageUrl && (
            <Box sx={{ mt: 3 }}>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                size="large"
                startIcon={<SearchIcon />}
              >
                Analyze Image
              </Button>
            </Box>
          )}
          {imageAnalyzed && (
            <Box sx={{ width: "100%", marginTop: 3, marginBottom: 3 }}>
              <EggCountDisplay
                computedEggCount={computedEggCount ?? null}
                onViewJsonClick={handleJsonModalOpen}
                predictions={predictions ?? []}
              />
            </Box>
          )}

          {isUploading && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 3 }}>
              <CircularProgressWithLabel value={uploadProgress} />
              <Typography variant="body2" color="text.secondary">
                Uploading your images...
              </Typography>
            </Box>
          )}
          {imagesSubmitted && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ display: "inline-flex" }}>
                <Typography variant="body1">
                  Image uploaded successfully!
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </Box>
    </Box>

      <Modal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        aria-labelledby="json-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            id="json-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Analyzed Results
          </Typography>
          <Box sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
            <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
              {JSON.stringify(predictions, null, 2)}
            </pre>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: "auto",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(predictions, null, 2)
                );
              }}
              startIcon={<ContentCopyIcon />}
            >
              Copy
            </Button>
            <Button
              variant="contained"
              component="a"
              href={`data:text/json;charset=utf-8,${encodeURIComponent(
                JSON.stringify(predictions, null, 2)
              )}`}
              download={`raw.json`}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </Box>
        </Box>
      </Modal>
      <ZoomModal
        open={modalOpen}
        onClose={handleModalClose}
        imageUrl={activeImage || ""}
        label={activeImageLabel || ""}
      />
    </>
  );
};

export default CloudYolo;
